// A compact replacement for react-tournament-bracket's <Bracket>.
//
// That component lays the games out as a perfect binary tree: every round
// doubles the vertical separation, so the drawing is `gameHeight * 2^(rounds-1)`
// tall no matter how few games there are. A double elimination bracket is deep
// (the loser bracket adds a round for every winner bracket round) but sparse, so
// a 13 contestant tournament came out 10000px tall for 25 games.
//
// Here the games are packed instead: each game without sources takes the next
// row, and every other game is centred on the games feeding it, which makes the
// height proportional to the number of games. The games themselves are still
// drawn by the library's BracketGame, so styling, hovering and clicking are
// unchanged.

import { BracketGame } from 'react-tournament-bracket';

// BracketGame draws a 200x82 box, with the two contestant rows at y 12..34.5 and
// y 34.5..57 of it
const GAME_WIDTH = 200;
const GAME_HEIGHT = 82;
const TOP_ROW_OFFSET = 23;
const BOTTOM_ROW_OFFSET = 46;
const BOX_MIDDLE_OFFSET = 34.5;

const COLUMN_GAP = 48;
const ROW_GAP = 6;
const PADDING = 12;

// The sides that come from winning another game, top one first. BracketCard
// renders with homeOnTop false, so the visitor is the top row.
function sourceSides(game) {
    return [
        { side: game.sides?.visitor, isTop: true },
        { side: game.sides?.home, isTop: false }
    ].filter(({ side }) => side?.seed?.sourceGame != null && side.seed.rank === 1);
}

function layoutBracket(root) {
    const nodes = new Map(); // game id -> placed game
    const links = [];
    let nextRow = 0;

    const place = (game, column) => {
        if (nodes.has(game.id)) return nodes.get(game.id);
        const node = { game, column, y: 0 };
        nodes.set(game.id, node);

        const sources = sourceSides(game);
        if (sources.length === 0) {
            // A game nothing feeds into starts a new row
            node.y = nextRow * (GAME_HEIGHT + ROW_GAP);
            nextRow++;
        } else {
            const sourceYs = sources.map(({ side, isTop }) => {
                const source = place(side.seed.sourceGame, column + 1);
                links.push({ source, target: node, isTop });
                return source.y;
            });
            node.y = (Math.min(...sourceYs) + Math.max(...sourceYs)) / 2;
        }
        return node;
    };

    place(root, 0);

    // Column 0 is the final, so columns are laid out right to left
    const placed = [...nodes.values()];
    const columns = Math.max(...placed.map(node => node.column)) + 1;
    const xOf = node => PADDING + (columns - 1 - node.column) * (GAME_WIDTH + COLUMN_GAP);

    return {
        games: placed.map(node => ({ ...node, x: xOf(node) })),
        links: links.map(({ source, target, isTop }) => {
            const startX = xOf(source) + GAME_WIDTH;
            const startY = source.y + BOX_MIDDLE_OFFSET;
            const endX = xOf(target);
            const endY = target.y + (isTop ? TOP_ROW_OFFSET : BOTTOM_ROW_OFFSET);
            const cornerX = startX + COLUMN_GAP / 2;
            return {
                key: `${source.game.id}-${target.game.id}`,
                d: `M${startX} ${startY} H${cornerX} V${endY} H${endX}`
            };
        }),
        width: columns * (GAME_WIDTH + COLUMN_GAP) - COLUMN_GAP + PADDING * 2,
        height: Math.max(nextRow * (GAME_HEIGHT + ROW_GAP) - ROW_GAP, GAME_HEIGHT) + PADDING * 2
    };
}

function CompactBracket({ game, GameComponent = BracketGame, homeOnTop = false, ...rest }) {
    const { games, links, width, height } = layoutBracket(game);
    return (
        <svg width={width} height={height}>
            <g>
                {links.map(link => (
                    <path key={link.key} d={link.d} fill="transparent" stroke="#999" />
                ))}
                {games.map(node => (
                    <GameComponent
                        {...rest}
                        key={node.game.id}
                        game={node.game}
                        homeOnTop={homeOnTop}
                        x={node.x}
                        y={node.y}
                    />
                ))}
            </g>
        </svg>
    );
}

export { layoutBracket };
export default CompactBracket;
